import type { InviteRole, OrganisationRole, TeamCapability } from '@ghostscreen/contracts';
import { i18n } from '../../i18n';
import { formatAdminDate, formatAdminRole } from '../admin/adminFormat';

export { formatAdminDate, formatAdminRole };

export type PeopleRoleFilter = 'all' | 'owner' | 'admin' | 'editor' | 'viewer';

export function matchesPeopleFilter(role: string, filter: PeopleRoleFilter): boolean {
  if (filter === 'all') return true;
  switch (filter) {
    case 'owner':
      return role === 'organisation_owner';
    case 'admin':
      return role === 'organisation_admin' || role === 'platform_admin' || role === 'location_manager';
    case 'editor':
      return role === 'content_manager';
    case 'viewer':
      return role === 'viewer';
    default:
      return true;
  }
}

export function isOwnerRole(role: string): boolean {
  return role === 'organisation_owner';
}

export const CAPABILITY_OPTIONS: readonly TeamCapability[] = [
  'manage_displays',
  'upload_assets',
  'edit_playlists',
  'view_reports',
  'manage_schedules',
];

export function formatCapability(capability: TeamCapability): string {
  return i18n.t(`teams:capabilities.${capability}`);
}

export function teamInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase() || '?';
}

export function formatInviteAge(iso: string): string {
  try {
    const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
    if (days === 0) return i18n.t('teams:pendingInvites.sentToday');
    return i18n.t('teams:pendingInvites.sentDaysAgo', { count: days });
  } catch {
    return formatAdminDate(iso);
  }
}

export const INVITE_ROLE_OPTIONS: readonly Exclude<InviteRole, 'location_manager'>[] = [
  'organisation_admin',
  'content_manager',
  'viewer',
];

export function formatInviteRole(role: string): string {
  return formatAdminRole(role as OrganisationRole);
}

export function parseInviteEmails(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[\s,;]+/)
        .map((part) => part.trim().toLowerCase())
        .filter((part) => part.includes('@')),
    ),
  ];
}
